import { StyleProvider, legacyLogicalPropertiesTransformer } from '@ant-design/cssinjs';
import { App as CommonApp, DocProvider } from 'zf-web-components';
import { RouterProvider } from 'react-router';
import { SharedProvider } from 'zf-web-shared';
import { observer } from 'mobx-react-lite';
import { Suspense, type ReactElement } from 'react';
import { ThemeConfig, useApp, Watermark } from '@components';
import { RootProvider } from '../../stores';
import { ErrorBoundary } from '../exception';
import { router } from './router';
import type { AppProps } from './types';

import 'zf-web-components/index.css';
import 'zf-web-components/grid.css';
import 'zf-web-components/widgets.css';
import 'zf-web-components/preview.css';
import 'zf-web-components/importer.css';
import '../../styles/index.less';

/**
 * 在最顶层使用多个 Provider 及组件包装
 *  - RootProvider
 *    使用 context 为子组件注入全局 store
 *
 *  - SharedProvider
 *    可以与微应用共享的数据
 *
 *  - StyleProvider
 *    hashPriority 默认为 low，配置为 high 后会移除 :where 选择器封装
 *
 *  - RouterProvider
 *    react-router 路由组建用 provider
 *
 *  - App
 *    是 Ant Design 的 App 组件
 *    通过 Context 提供上下文方法调用，可使用 useApp 直接使用 message 等方法
 *
 * ReactRouter v6.4 - Recommend all web projects use createBrowserRouter.
 */
function AppPage(props: AppProps): ReactElement {
  const { app } = useApp();

  return (
    <RootProvider value={props.store}>
      <SharedProvider value={props.store.shared}>
        <StyleProvider hashPriority='high' transformers={[legacyLogicalPropertiesTransformer]}>
          <ThemeConfig store={props.store}>
            <DocProvider host={`/apidoc/${app}/manual`}>
              <Watermark>
                <ErrorBoundary>
                  <CommonApp>
                    <Suspense fallback={<>loading...</>}>
                      <RouterProvider router={router.build()} />
                    </Suspense>
                  </CommonApp>
                </ErrorBoundary>
              </Watermark>
            </DocProvider>
          </ThemeConfig>
        </StyleProvider>
      </SharedProvider>
    </RootProvider>
  );
}

export const App = observer(AppPage);
